import {
	useCallback,
} from 'react';

import { defaultSorter } from '../../utils/slides';

/**
 * Generic slide transformation hook
 * Provides reusable slide transformation utilities that can be used across different features
 */
export const useSlideTransformation = (isInFolderSchedulingEnabled = false) => {
	// Basic slide transformation - generic enough to be reused elsewhere
	const transformSlide = useCallback((slide) => {
		if (!slide) return null;

		return {
			id:               slide.id,
			name:             slide.name || slide.description || 'Untitled Slide',
			duration:         Math.round(slide.duration) || 0,
			slots:            slide.itemType === 'folder' ? slide.folderSlots : 1, // Folders use folderSlots, others use 1
			order:            slide.order ?? slide.id,
			startDate:        slide.scheduled && slide.scheduling?.start_date ? slide.scheduling.start_date : '',
			endDate:          slide.scheduled && slide.scheduling?.end_date ? slide.scheduling.end_date : '',
			kind:             slide.kind || '',
			file:             slide.file || '',
			itemType:         slide.itemType || 'slide', // Preserve itemType for downstream usage
			folderFiles:      slide.folderFiles,         // Pass-through for folder rows
			slideDeckSlideID: slide.slideDeckSlideID,    // Pass-through for In-Folder Scheduling routing
			folderId:         slide.folderId,
		};
	}, []);

	// Transform the nested slides structure from API - generic transformation
	// `deckFlagOverride`: the deck OWNER's is_in_folder_scheduling_enabled flag. Pass it
	// for cross-account (published feed) decks so schedules are applied the way the
	// publisher configured them — matching bronco's render path, which uses the deck's
	// account flag. Undefined → fall back to the viewing account's flag.
	const transformApiSlides = useCallback((slideItems, deckFlagOverride) => {
		if (!Array.isArray(slideItems)) return [];
		const schedulingEnabled = typeof deckFlagOverride === 'boolean'
			? deckFlagOverride
			: isInFolderSchedulingEnabled;

		// First sort by order to ensure consistent ordering
		const sortedSlides = [ ...slideItems, ].sort((a, b) => {
			return (a.order || 0) - (b.order || 0);
		});

		return sortedSlides
			.map((slideItem) => {
				let contentItem;
				let itemType;

				// Determine which type of content this is
				if (slideItem.slide) {
					contentItem = slideItem.slide;
					itemType = 'slide';
				} else if (slideItem.gallery_file) {
					contentItem = slideItem.gallery_file;
					itemType = 'gallery_file';
				} else if (slideItem.folder) {
					contentItem = slideItem.folder;
					itemType = 'folder';
				} else {
					return null;
				}

				// For folders, calculate slot count based on folder_slots or number of files
				let folderSlots = 1;
				if (itemType === 'folder') {
					const filesCount = contentItem.files?.length || 0;
					if (slideItem.folder_slots !== null && slideItem.folder_slots > 0) {
						// Use folder_slots if specified
						folderSlots = Math.min(slideItem.folder_slots, filesCount);
					} else {
						// Otherwise use all files in the folder
						folderSlots = filesCount;
					}
				}

				// Transform content based on type
				return {
					id:          contentItem.id,
					name:        itemType === 'folder' ? `Folder: ${contentItem.label || 'Untitled Folder'}` : (contentItem.description || contentItem.name || contentItem.file_name || 'Untitled Content'),
					file:        contentItem.file || contentItem.path || '',
					kind:        contentItem.kind || contentItem.mime_type || '',
					description: contentItem.description || contentItem.name || contentItem.file_name || '',
					duration:    slideItem.duration || contentItem.duration || 15,
					scheduled:   slideItem.scheduled || false,
					scheduling:  slideItem.scheduling || {},
					resolution:  contentItem.resolution || '',
					order:       slideItem.order ?? 1,
					file_size:   contentItem.file_size || 0,
					itemType,    // Track what type of content this is
					folderSlots, // For folders, track how many slots/files will be used
					// For folders: include files array so VCL editor can show per-file expand.
					// Sort to match In-Folder Scheduling order: order ASC, then file_name ASC.
					// Annotate each file with its per-file schedule (active flag + date ranges)
					// so the VCL editor correctly excludes unscheduled files from the badge/expand.
					folderFiles: (() => {
						if (itemType !== 'folder') return undefined;
						const scheduleByFileId = {};
						const hasScheduleRows = Array.isArray(slideItem.folder_schedules) && slideItem.folder_schedules.length > 0;
						if (Array.isArray(slideItem.folder_schedules)) {
							for (const s of slideItem.folder_schedules) {
								scheduleByFileId[String(s.gallery_file_id)] = s;
							}
						}
						return [ ...(contentItem.files || []), ]
							.sort(defaultSorter)
							.map((file) => {
								// Schedule rows only apply while the account flag is ON — flag off
								// disables the feature entirely and every file plays (PO ruling).
								const sched = schedulingEnabled ? scheduleByFileId[String(file.id)] : undefined;
								const defaultActive = (hasScheduleRows && schedulingEnabled) ? false : true;
								return {
									...file,
									duration:                slideItem.duration || file.duration || 15,
									folder_file_active:      sched ? sched.active : defaultActive,
									folder_file_date_ranges: sched ? (sched.date_ranges || []) : [],
								};
							});
					})(),

					slideDeckSlideID:  slideItem.id,                   // id of the slide_deck_slide row
					folderId:          itemType === 'folder' ? contentItem.id : undefined,
				};
			})
			.filter(Boolean);
	}, [ isInFolderSchedulingEnabled, ]);

	return {
		transformSlide,
		transformApiSlides,
	};
};